"use client";

import { useEffect, useRef, useState } from "react";
import { useRouter } from "next/navigation";
import { useAuth } from "@/contexts/auth-context";
import { AppBootScreen } from "@/components/auth-guard";
import {
  clearSignupIntent,
  markSignupStarted,
  trackSignupFailed,
  trackSignupPageViewed,
} from "@/lib/signup-funnel";
import { TrustSplitShell } from "@/components/trust-panel";

type Mode = "signin" | "signup" | "reset";

const FRIENDLY_ERRORS: Record<string, string> = {
  "auth/invalid-credential": "Email or password is incorrect.",
  "auth/invalid-email": "That doesn't look like a valid email address.",
  "auth/wrong-password": "Email or password is incorrect.",
  "auth/user-not-found": "No account with that email. Try signing up.",
  "auth/email-already-in-use": "An account with that email already exists. Try signing in.",
  "auth/weak-password": "Password must be at least 6 characters.",
  "auth/too-many-requests": "Too many attempts. Wait a minute and try again.",
};

function friendlyError(err: unknown, fallback: string): string {
  if (err instanceof Error) {
    const code = (err as { code?: string }).code;
    if (code && FRIENDLY_ERRORS[code]) return FRIENDLY_ERRORS[code];
    return err.message;
  }
  return fallback;
}

/** The post-login destination from the `?next=` param, if it's a safe
 * same-origin path. AuthGuard sets it when it bounces a signed-out user off a
 * deep link. Only same-origin absolute paths are honored — reject absolute
 * URLs and protocol-relative `//host` to avoid an open redirect. */
function safeNextTarget(): string | null {
  if (typeof window === "undefined") return null;
  const raw = new URLSearchParams(window.location.search).get("next");
  if (!raw || !raw.startsWith("/") || raw.startsWith("//")) return null;
  return raw;
}

export default function LoginPage() {
  const {
    user,
    loading,
    signIn,
    signInWithEmail,
    signUpWithEmail,
    resetPassword,
    redirectSignInError,
    clearRedirectSignInError,
  } = useAuth();
  const router = useRouter();
  const [mode, setMode] = useState<Mode>("signin");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [error, setError] = useState("");
  const [info, setInfo] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!loading && user) {
      router.replace(safeNextTarget() ?? "/");
    }
  }, [user, loading, router]);

  // Read initial mode from ?mode= param. Runs once in an effect, not a useState
  // initializer — `window` is undefined during SSR and would hydrate wrong.
  useEffect(() => {
    const urlMode = new URLSearchParams(window.location.search).get("mode");
    if (urlMode === "signup" || urlMode === "reset") setMode(urlMode);
  }, []);

  // signup_page_viewed: once per page load, the first time the signup form is
  // shown (deep link or the tab). The ref latch survives mode toggles.
  const signupViewed = useRef(false);
  useEffect(() => {
    if (mode !== "signup" || signupViewed.current) return;
    signupViewed.current = true;
    trackSignupPageViewed();
  }, [mode]);

  // Keep URL in sync with the displayed mode so reload / deep-link / analytics
  // can distinguish states. Preserves the attribution query parameters.
  function switchMode(newMode: Mode) {
    setMode(newMode);
    setError("");
    setInfo("");
    clearRedirectSignInError();
    const sp = new URLSearchParams(window.location.search);
    if (newMode === "signin") sp.delete("mode");
    else sp.set("mode", newMode);
    const qs = sp.toString();
    router.replace(`/login${qs ? `?${qs}` : ""}`);
  }

  async function handleGoogle() {
    try {
      setError("");
      setInfo("");
      clearRedirectSignInError();
      // In signup mode the Google button IS the account-creation action — mark
      // the funnel start so signup_completed can attribute the source/method.
      // In sign-in mode, clear any leftover intent so an abandoned signup can't
      // misattribute this (possibly different) user's completion.
      if (mode === "signup") markSignupStarted("google");
      else clearSignupIntent();
      await signIn();
    } catch (err) {
      if (mode === "signup") trackSignupFailed("google", err);
      if (err instanceof Error && err.message.includes("popup-closed")) return;
      setError(friendlyError(err, "Sign in failed. Please try again."));
    }
  }

  async function handleEmailSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError("");
    setInfo("");
    clearRedirectSignInError();
    const trimmed = email.trim();
    if (!trimmed) {
      setError("Email is required.");
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) {
      setError("That doesn't look like a valid email address.");
      return;
    }
    if (mode !== "reset" && !password) {
      setError("Password is required.");
      return;
    }
    setBusy(true);
    try {
      if (mode === "signin") {
        clearSignupIntent(); // not a signup — drop any stale attribution intent
        await signInWithEmail(email, password);
      } else if (mode === "signup") {
        markSignupStarted("email");
        await signUpWithEmail(email, password, displayName);
      } else {
        await resetPassword(email);
        setInfo("Password reset email sent. Check your inbox.");
      }
    } catch (err) {
      if (mode === "signup") trackSignupFailed("email", err);
      setError(friendlyError(err, "Sign in failed. Please try again."));
    } finally {
      setBusy(false);
    }
  }

  const submitLabel =
    mode === "signin" ? "Sign in" : mode === "signup" ? "Create account" : "Send reset email";

  // While the initial auth state is still resolving — which includes the
  // several-second window after a Google redirect round-trip lands back here —
  // or when a signed-in user is about to be bounced to their destination by
  // the effect above, show the boot spinner instead of flashing the sign-in
  // form mid-sign-in (reported 2026-07-03, mobile).
  if (loading || user) return <AppBootScreen />;

  return (
    <TrustSplitShell>
      <div className="flex flex-col gap-5">
        <div>
          <div className="flex items-center gap-2.5">
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img src="/favicon.svg" alt="" className="h-8 w-8" />
            <span className="text-sm font-semibold text-gray-900">Deliverables AI</span>
          </div>
          <h1 className="mt-6 text-2xl font-semibold tracking-tight text-gray-900">
            {mode === "signup"
              ? "Create your account"
              : mode === "reset"
                ? "Reset your password"
                : "Sign in"}
          </h1>
          <p className="text-sm text-gray-500 mt-1.5">
            {mode === "signup"
              ? // Echoes the site's "Start free" promise: the signup seed lands
                // with no card on file (ENG-5229, ENG-5571).
                "Start your free trial — no credit card required."
              : mode === "reset"
                ? "We'll email you a link to set a new password."
                : "Welcome back — sign in to continue."}
          </p>
        </div>

        <button
          type="button"
          onClick={handleGoogle}
          disabled={busy}
          className="w-full flex items-center justify-center gap-3 px-4 py-2.5 rounded-lg border border-gray-300 bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 transition-colors"
        >
          <svg viewBox="0 0 24 24" className="w-4 h-4" aria-hidden="true">
            <path
              fill="#4285F4"
              d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
            />
            <path
              fill="#34A853"
              d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
            />
            <path
              fill="#FBBC05"
              d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z"
            />
            <path
              fill="#EA4335"
              d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
            />
          </svg>
          Continue with Google
        </button>

        <div className="flex items-center gap-3 text-[11px] uppercase tracking-wider text-gray-400">
          <span className="flex-1 h-px bg-gray-200" />
          or
          <span className="flex-1 h-px bg-gray-200" />
        </div>

        {/* noValidate disables native browser constraint UI so our JS setError
            messages run instead of the browser's focus-snap with no copy. */}
        <form noValidate onSubmit={handleEmailSubmit} className="flex flex-col gap-3">
          {mode === "signup" && (
            <input
              type="text"
              id="name"
              name="name"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              placeholder="Your name"
              autoComplete="name"
              className="px-3 py-2 rounded-lg border border-gray-300 bg-white text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none focus:border-gray-400"
            />
          )}
          <input
            type="email"
            id="email"
            name="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="Email"
            autoComplete="email"
            className="px-3 py-2 rounded-lg border border-gray-300 bg-white text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none focus:border-gray-400"
          />
          {mode !== "reset" && (
            <input
              type="password"
              id="password"
              name="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Password"
              autoComplete={mode === "signup" ? "new-password" : "current-password"}
              className="px-3 py-2 rounded-lg border border-gray-300 bg-white text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none focus:border-gray-400"
            />
          )}
          <button
            type="submit"
            disabled={busy}
            className="px-4 py-2.5 rounded-lg bg-gray-900 text-white text-sm font-medium hover:bg-gray-800 disabled:opacity-50 transition-colors"
          >
            {busy ? "…" : submitLabel}
          </button>
        </form>

        {(error || redirectSignInError) && (
          <p className="text-sm text-red-600 text-center">{error || redirectSignInError}</p>
        )}
        {info && <p className="text-sm text-green-700 text-center">{info}</p>}

        <div className="text-xs text-gray-500 text-center flex flex-col gap-1">
          {mode === "signin" && (
            <>
              <button
                type="button"
                onClick={() => switchMode("signup")}
                className="hover:text-gray-700"
              >
                Don&apos;t have an account? <span className="underline">Sign up</span>
              </button>
              <button
                type="button"
                onClick={() => switchMode("reset")}
                className="hover:text-gray-700"
              >
                <span className="underline">Forgot your password?</span>
              </button>
            </>
          )}
          {mode === "signup" && (
            <button
              type="button"
              onClick={() => switchMode("signin")}
              className="hover:text-gray-700"
            >
              Already have an account? <span className="underline">Sign in</span>
            </button>
          )}
          {mode === "reset" && (
            <button
              type="button"
              onClick={() => switchMode("signin")}
              className="hover:text-gray-700"
            >
              <span className="underline">Back to sign in</span>
            </button>
          )}
        </div>

        <p className="text-[11px] text-gray-500 text-center leading-relaxed">
          By continuing you agree to our{" "}
          <a
            href="https://deliverables.ai/terms-of-service"
            target="_blank"
            rel="noreferrer"
            className="underline hover:text-gray-700"
          >
            Terms of Service
          </a>{" "}
          and{" "}
          <a
            href="https://deliverables.ai/privacy-policy"
            target="_blank"
            rel="noreferrer"
            className="underline hover:text-gray-700"
          >
            Privacy Policy
          </a>
          .
        </p>
      </div>
    </TrustSplitShell>
  );
}
